import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import SegmentChartCardKebabMenu from "./SegmentChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeSaleByCustomerCategoryModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

function formatAmountCell(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function formatYAxisTick(v) {
  const nv = Number(v);
  if (!Number.isFinite(nv)) return "";
  if (nv >= 1000) return `${Math.round(nv / 1000)}K`;
  return String(Math.round(nv));
}

/**
 * @param {object} props
 * @param {import('./dashboardChartsModel.js').SaleByCustomerCategoryModel} props.model
 */
function FooterLegend({ model }) {
  const rows = useMemo(() => {
    const categories = model?.categories ?? [];
    const amounts = model?.amounts ?? [];
    const colors = model?.colors ?? [];
    const n = Math.min(categories.length, amounts.length, colors.length);
    let total = 0;
    for (let i = 0; i < n; i += 1) {
      const v = Number(amounts[i]);
      total += Number.isFinite(v) ? v : 0;
    }
    const out = [];
    for (let i = 0; i < n; i += 1) {
      const v = Number(amounts[i]);
      const value = Number.isFinite(v) ? v : 0;
      const pct = total > 0 ? Math.round((value / total) * 100) : 0;
      out.push({
        key: `cc-leg-${i}`,
        label: categories[i] ?? "",
        value,
        pct,
        color: colors[i] ?? "#5CC7C1",
      });
    }
    return out;
  }, [model]);

  return (
    <div className="pos-customer-cat__legend-row" role="list">
      {rows.map((row) => (
        <div key={row.key} className="pos-customer-cat__legend-group" role="listitem">
          <div className="pos-customer-cat__legend-head">
            <span className="pos-customer-cat__swatch" style={{ background: row.color }} aria-hidden />
            <span className="pos-customer-cat__legend-name">{row.label}</span>
          </div>
          <div className="pos-customer-cat__legend-meta">
            <span className="pos-customer-cat__legend-amount">{formatAmountCell(row.value)}</span>
            <span className="pos-customer-cat__legend-pct" style={{fontSize:'10px',color:'#A4A4A4'}}>({row.pct}%)</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * @param {{ model: import('./dashboardChartsModel.js').SaleByCustomerCategoryModel }} props
 */
function CustomerCategoryDonutBody({ model }) {
  const { t } = useTranslation();
  const { labels, values, colors, total } = useMemo(() => {
    const c = model?.categories ?? [];
    const a = model?.amounts ?? [];
    const col = model?.colors ?? [];
    const n = Math.min(c.length, a.length, col.length);
    const labelsN = [];
    const valuesN = [];
    const colorsN = [];
    let tot = 0;
    for (let i = 0; i < n; i += 1) {
      labelsN.push(c[i] ?? "");
      const v = Number(a[i]);
      const vv = Number.isFinite(v) ? v : 0;
      valuesN.push(vv);
      tot += vv;
      colorsN.push(col[i] ?? "#5CC7C1");
    }
    return { labels: labelsN, values: valuesN, colors: colorsN, total: tot };
  }, [model]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [{ data: values, backgroundColor: colors, borderWidth: 0 }],
    }),
    [labels, values, colors],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      interaction: { mode: "nearest", intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const lbl = items[0]?.label;
              return lbl ? [`${lbl}:`] : [];
            },
            label: (ctx) => {
              const v = ctx.parsed;
              const pct = total ? Math.round((v / total) * 100) : 0;
              return `${formatAmountCell(v)} (${pct}%)`;
            },
          },
        },
      },
    }),
    [total],
  );

  return (
    <div className="pos-customer-cat-donut" aria-label={t("Sale by customer category", { defaultValue: "Sale by customer category" })}>
      <div className="pos-customer-cat-donut__chart">
        <div className="pos-customer-cat-donut__canvas">
          <Doughnut data={chartData} options={options} />
        </div>
        <div className="pos-customer-cat-donut__center">
          <span className="pos-customer-cat-donut__center-label">{t("Total Sale", { defaultValue: "Total Sale" })}</span>
          <span className="pos-customer-cat-donut__center-value">{formatAmountCell(total)}</span>
        </div>
      </div>
      <FooterLegend model={model} />
    </div>
  );
}

/**
 * @param {{ model: import('./dashboardChartsModel.js').SaleByCustomerCategoryModel }} props
 */
function CustomerCategoryBarBody({ model }) {
  const { t } = useTranslation();

  const { chartData, options } = useMemo(() => {
    const c = model?.categories ?? [];
    const a = model?.amounts ?? [];
    const col = model?.colors ?? [];
    const n = Math.min(c.length, a.length, col.length);
    const xL = [];
    const data = [];
    const bg = [];
    for (let i = 0; i < n; i += 1) {
      xL.push(c[i] ?? "");
      const v = Number(a[i]);
      data.push(Number.isFinite(v) ? v : 0);
      bg.push(col[i] ?? "#5CC7C1");
    }
    const chart = {
      labels: xL,
      datasets: [
        {
          label: t("Amount", { defaultValue: "Amount" }),
          data,
          backgroundColor: bg,
          maxBarThickness: 120,
          borderRadius: 0,
          borderSkipped: false,
        },
      ],
    };
    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const lbl = items[0]?.label;
              return lbl ? [`${lbl}:`] : [];
            },
            label: (ctx) => formatAmountCell(Number(ctx.parsed?.y)),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { family: "'Segoe UI', Roboto, sans-serif", size: 11 },
            color: "#666666",
          },
        },
        y: {
          min: 0,
          border: { display: false },
          grid: { color: "#E6E6E6" },
          title: {
            display: true,
            text: t("Amount", { defaultValue: "Amount" }),
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 12 },
          },
          ticks: {
            font: { family: "Roboto, sans-serif", size: 11 },
            color: "#666666",
            callback: (v) => formatYAxisTick(v),
          },
        },
      },
    };
    return { chartData: chart, options: opts };
  }, [model, t]);

  return (
    <div className="pos-customer-cat-bar">
      <div className="pos-customer-cat-bar__plot">
        <Bar data={chartData} options={options} />
      </div>
      <FooterLegend model={model} />
    </div>
  );
}

/**
 * @param {{ model: import('./dashboardChartsModel.js').SaleByCustomerCategoryModel }} props
 */
function CustomerCategoryTableBody({ model }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"label"|"amount"|"pct"} */ ("amount"),
    dir: /** @type {"asc"|"desc"} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: "amount", dir: "desc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const baseRows = useMemo(() => {
    const c = model?.categories ?? [];
    const a = model?.amounts ?? [];
    const n = Math.min(c.length, a.length);
    let total = 0;
    const raw = [];
    for (let i = 0; i < n; i += 1) {
      const v = Number(a[i]);
      const val = Number.isFinite(v) ? v : 0;
      total += val;
      raw.push({ key: `cc-td-${i}`, label: c[i] ?? "", amount: val });
    }
    return raw.map((r) => ({
      ...r,
      pct: total > 0 ? Math.round((r.amount / total) * 100) : 0,
    }));
  }, [model]);

  const rows = useMemo(() => {
    const copy = baseRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "label":
          cmp = String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" });
          break;
        case "amount":
          cmp = a.amount - b.amount;
          break;
        case "pct":
          cmp = a.pct - b.pct;
          break;
        default:
          cmp = 0;
      }
      return cmp * mul;
    });
    return copy;
  }, [baseRows, sort]);

  const thBase = "pos-customer-cat-table__th";
  const thSorted = "pos-customer-cat-table__th--sorted";

  return (
    <div className="pos-customer-cat-table" role="table" aria-label={t("Sale by customer category", { defaultValue: "Sale by customer category" })}>
      <div className="pos-customer-cat-table__head" role="rowgroup">
        <div className="pos-customer-cat-table__tr pos-customer-cat-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-customer-cat-table__th--customer"
            ariaSortLabel={t("Sort by customer", { defaultValue: "Sort by customer" })}
            sorted={sort.key === "label"}
            direction={sort.dir}
            onSort={() => toggleSort("label")}
          >
            {t("Customer", { defaultValue: "Customer" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-customer-cat-table__th--sale"
            ariaSortLabel={t("Sort by sale", { defaultValue: "Sort by sale" })}
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
          >
            {t("Sale", { defaultValue: "Sale" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-customer-cat-table__th--pct"
            ariaSortLabel={t("Sort by percent", { defaultValue: "Sort by percent" })}
            sorted={sort.key === "pct"}
            direction={sort.dir}
            onSort={() => toggleSort("pct")}
          >
            {t("%", { defaultValue: "%" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-customer-cat-table__scroll" role="rowgroup">
        {rows.map((row) => (
          <div key={row.key} className="pos-customer-cat-table__tr" role="row">
            <div className="pos-customer-cat-table__td pos-customer-cat-table__td--customer" role="cell">
              {row.label}
            </div>
            <div className="pos-customer-cat-table__td pos-customer-cat-table__td--sale" role="cell">
              {formatAmountCell(row.amount)}
            </div>
            <div className="pos-customer-cat-table__td pos-customer-cat-table__td--pct" role="cell">
              {row.pct}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Figma “Sale by customer category” — donut / bar / table + kebab (Pie Chart, Chart, Table, Download, Remove).
 *
 * @param {object} props
 * @param {import('./dashboardChartsModel.js').SaleByCustomerCategoryModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function SaleByCustomerCategoryChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewKind, setViewKind] = useState(/** @type {'donut'|'bar'|'table'} */ ("donut"));
  const mergedModel = useMemo(() => mergeSaleByCustomerCategoryModel(model ?? null), [model]);

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("customer-category"));
  }, []);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--customer-category${
        viewKind === "table" ? " pos-dashboard-chart-card--customer-category-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header pos-dashboard-chart-card__header--customer-category">
        <h2 className="pos-dashboard-chart-card__title">
          {t("graphSaleByCustomerCategory", { defaultValue: "Sale by customer category" })}
        </h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <SegmentChartCardKebabMenu
            viewKind={viewKind}
            onViewKindChange={setViewKind}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--customer-category${
          viewKind === "table" ? " pos-dashboard-chart-card__body--customer-category-table" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--customer-category">
          {viewKind === "donut" ? <CustomerCategoryDonutBody model={mergedModel} /> : null}
          {viewKind === "bar" ? <CustomerCategoryBarBody model={mergedModel} /> : null}
          {viewKind === "table" ? <CustomerCategoryTableBody model={mergedModel} /> : null}
        </div>
      </div>
    </div>
  );
}

export default SaleByCustomerCategoryChartCard;
